import { z } from 'zod';
import type { RoleKey } from './enums.js';
import { fillPlaceholders } from './leases.js';

// ---------- Representative services agreements (template → PDF → built-in e-signing) ----------

export const REP_CONTRACT_DEFAULT_TEMPLATE = 'Representative Services Agreement (standard)';
export const REP_CONTRACT_TITLE = 'Representative Services Agreement';

/**
 * Placeholders a representative contract template can use. Values come from the contract (rep details entered when it
 * is prepared) and the company settings (Admin → Contract templates).
 */
export const CONTRACT_PLACEHOLDERS = [
  { key: 'contract_number', label: 'Contract number (CTR-000001)', sample: 'CTR-000042' },
  { key: 'today', label: 'Date the PDF is generated', sample: 'October 24, 2026' },
  { key: 'agreement_date', label: 'Agreement date', sample: 'October 24, 2026' },
  { key: 'company_legal_name', label: 'Company legal name', sample: 'EVEREST VENTURES GROUP LLC' },
  { key: 'company_dba', label: 'Company "doing business as" name', sample: 'The Residential Address' },
  { key: 'company_address', label: 'Company business / registered address', sample: '6204 Forest Hill Blvd, Apt 211, West Palm Beach, Florida 33415' },
  { key: 'company_email', label: 'Company email', sample: 'support@theresidentialaddress.com' },
  { key: 'company_phone', label: 'Company phone', sample: '(645) 232-1990' },
  { key: 'company_state', label: 'Company state of formation', sample: 'Florida' },
  { key: 'company_registration_number', label: 'Company entity / registration number', sample: 'L26000069113' },
  { key: 'rep_name', label: 'Representative full legal name', sample: 'Jordan Avery Blake' },
  { key: 'rep_address', label: 'Representative residential / business address', sample: '1200 Sample Lane, Orlando, Florida 32801' },
  { key: 'rep_email', label: 'Representative email', sample: 'jordan.blake@example.com' },
  { key: 'rep_phone', label: 'Representative phone', sample: '+1 407 555 0142' },
  { key: 'property_address', label: 'Property address (where the representative receives mail)', sample: '1200 Sample Lane, Orlando, Florida 32801' },
  { key: 'start_date', label: 'Start date', sample: 'October 24, 2026' },
  { key: 'end_date', label: 'End date', sample: 'October 23, 2027' },
  { key: 'rate_per_client', label: 'Compensation per client', sample: '$100.00' },
  { key: 'breach_amount', label: 'Serious card-related breach amount (2 × rate per client)', sample: '$200.00' },
  { key: 'governing_state', label: 'Governing law state', sample: 'Florida' },
  { key: 'venue_county', label: 'Venue county', sample: 'Palm Beach County' },
] as const;
export type ContractPlaceholder = (typeof CONTRACT_PLACEHOLDERS)[number]['key'];
export const CONTRACT_PLACEHOLDER_KEYS = CONTRACT_PLACEHOLDERS.map((p) => p.key) as ContractPlaceholder[];
export const CONTRACT_SAMPLE_VALUES = Object.fromEntries(CONTRACT_PLACEHOLDERS.map((p) => [p.key, p.sample])) as Record<ContractPlaceholder, string>;

export function renderContractTemplate(body: string, values: Partial<Record<ContractPlaceholder, string | null | undefined>>) {
  return fillPlaceholders(body, values, CONTRACT_PLACEHOLDER_KEYS);
}

/** "$100.00" (USD) or "EUR 100.00". */
export function contractMoney(cents: number, currency = 'USD') {
  return `${currency === 'USD' ? '$' : `${currency} `}${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
/** The serious card-related breach amount: twice the rate per client. */
export const contractBreachCents = (rateCents: number) => rateCents * 2;

/** Default end date: one year after the start, minus one day (2026-10-24 → 2027-10-23). */
export function contractDefaultEndDate(start: string): string {
  const [y, m, d] = start.split('-').map(Number);
  return new Date(Date.UTC(y + 1, m - 1, d) - 86_400_000).toISOString().slice(0, 10);
}

/** Title printed under Party A's signature by default, from the sender's role (the user's own job title wins). */
export const CONTRACT_SIGNER_TITLE_BY_ROLE: Partial<Record<RoleKey, string>> = {
  system_administrator: 'Owner',
  executive_oversight: 'Owner',
  operations_supervisor: 'Operations Supervisor',
  shipping_operations_manager: 'Shipping Operations Manager',
  operations_coordinator: 'Operations Coordinator',
};
export function contractSignerTitle(roles: readonly string[], userTitle?: string | null): string {
  if (userTitle?.trim()) return userTitle.trim();
  for (const r of ['system_administrator', 'executive_oversight', 'operations_supervisor', 'shipping_operations_manager', 'operations_coordinator'] as RoleKey[])
    if (roles.includes(r)) return CONTRACT_SIGNER_TITLE_BY_ROLE[r]!;
  return '';
}

/**
 * Signature blocks of the agreement (section 25): Party A (the company, signed by an authorized representative) and
 * Party B (the representative). Signature and date lines are drawn under these lines.
 */
export function contractPartyBlockLines(p: { role: 'company'; dba: string; legalName: string; signerName: string | null; signerTitle: string | null } | { role: 'representative'; name: string }): { heading: string; lines: string[] } {
  const blank = '____________________';
  if (p.role === 'company')
    return {
      heading: `PARTY A — ${p.dba || p.legalName}`,
      lines: [`Legal Entity: ${p.legalName}`, `Authorized Representative Name: ${p.signerName?.trim() || blank}`, `Title: ${p.signerTitle?.trim() || blank}`],
    };
  return { heading: 'PARTY B — REPRESENTATIVE', lines: [`Full Legal Name: ${p.name?.trim() || blank}`] };
}

/** Company details printed on representative contracts (Admin → Contract templates). */
export const contractCompanySettingsInput = z.object({
  legalName: z.string().trim().min(1, 'Required').max(200),
  dba: z.string().trim().max(200).default(''),
  address: z.string().trim().max(500).default(''),
  email: z.string().trim().email().max(200).or(z.literal('')).default(''),
  phone: z.string().trim().max(60).default(''),
  stateOfFormation: z.string().trim().max(100).default(''),
  registrationNumber: z.string().trim().max(100).default(''),
  governingState: z.string().trim().max(100).default(''),
  venueCounty: z.string().trim().max(200).default(''),
  defaultRateCents: z.number().int().min(0).max(10_000_000).default(10_000),
  currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code').default('USD'),
});
export type ContractCompanySettings = z.infer<typeof contractCompanySettingsInput>;
export const CONTRACT_COMPANY_DEFAULTS: ContractCompanySettings = {
  legalName: 'EVEREST VENTURES GROUP LLC',
  dba: 'The Residential Address',
  address: '6204 Forest Hill Blvd, Apt 211, West Palm Beach, Florida 33415',
  email: 'support@theresidentialaddress.com',
  phone: '(645) 232-1990',
  stateOfFormation: 'Florida',
  registrationNumber: 'L26000069113',
  governingState: 'Florida',
  venueCounty: 'Palm Beach County',
  defaultRateCents: 10_000,
  currency: 'USD',
};

export const contractTemplateInput = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  body: z.string().max(100_000).refine((v) => v.trim().length > 0, 'Write the template text'),
  active: z.boolean().default(true),
  isDefault: z.boolean().default(false),
});
export type ContractTemplateInput = z.infer<typeof contractTemplateInput>;

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const reqText = (max: number) => z.string().trim().min(1, 'Required').max(max);
const optText = (max: number) => z.string().trim().max(max).nullish().transform((v) => (v ? v : null));
const optEmail = z.string().trim().email().max(200).nullish().or(z.literal('')).transform((v) => (v ? v : null));

/** Details of a representative agreement (prepared from the template, then sent for e-signature). */
const repContractFields = z.object({
  repName: reqText(200),
  repAddress: optText(500),
  repEmail: optEmail,
  repPhone: optText(60),
  /** One of the representative's inventory addresses (its address is printed when propertyAddress is empty). */
  addressId: z.string().uuid().nullish(),
  propertyAddress: optText(500),
  agreementDate: dateOnly,
  startDate: dateOnly,
  endDate: dateOnly,
  ratePerClientCents: z.number().int().min(0).max(10_000_000),
  currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/).default('USD'),
  governingState: reqText(100),
  venueCounty: reqText(200),
  /** Party A signer (a portal user who may send contracts; default the sender) and the title printed under it. */
  companySignerUserId: z.string().uuid().nullish(),
  companySignerTitle: optText(200),
  templateId: z.string().uuid().nullish(),
  title: optText(200),
  /** Save changed name / address / email / phone back to the representative's profile. */
  updateRepProfile: z.boolean().default(false),
});

export const repContractInput = repContractFields
  .extend({
    representativeId: z.string().uuid().nullish(),
    /** A representative created together with the contract (Contracts → New contract → new representative). */
    newRepresentative: z.boolean().default(false),
  })
  .refine((v) => v.endDate >= v.startDate, { message: 'The end date must be on or after the start date', path: ['endDate'] })
  .refine((v) => v.newRepresentative || !!v.representativeId, { message: 'Pick the representative', path: ['representativeId'] });
export type RepContractInput = z.infer<typeof repContractInput>;
export const repContractUpdateInput = repContractFields.partial();
export type RepContractUpdateInput = z.infer<typeof repContractUpdateInput>;

/** Send for signature: the representative signs first by link, then Party A countersigns in the portal. */
export const repContractSendInput = z.object({
  /** Party A countersigner (default: the contract's Party A signer, else the sender). */
  countersignerUserId: z.string().uuid().nullish(),
  /** Title printed under Party A's signature (a change regenerates the PDF before sending). */
  countersignerTitle: optText(200).optional(),
  repEmail: optEmail.optional(),
  repPhone: optText(60).optional(),
});

/** What the representative contract looks like to people (derived from the contract and its signature request). */
export const CONTRACT_ESIGN_STATUSES = ['draft', 'sent', 'viewed', 'signed_by_rep', 'executed', 'declined', 'expired', 'terminated'] as const;
export type ContractEsignStatus = (typeof CONTRACT_ESIGN_STATUSES)[number];
export const CONTRACT_ESIGN_STATUS_LABELS: Record<ContractEsignStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  signed_by_rep: 'Signed by rep',
  executed: 'Executed',
  declined: 'Declined',
  expired: 'Expired',
  terminated: 'Terminated',
};
export const CONTRACT_SIGNER_ROLES = ['representative', 'company'] as const;
export type ContractSignerRole = (typeof CONTRACT_SIGNER_ROLES)[number];
export const CONTRACT_SIGNER_ROLE_LABELS: Record<ContractSignerRole, string> = { representative: 'Party B — Representative', company: 'Party A — Company' };
/** Days before the end date the rep ops team is reminded. */
export const CONTRACT_REMINDER_DAYS = 30;
